import {
  TEMPLATE_APPROVAL_HINTS,
  TEMPLATE_APPROVAL_LABELS,
  type TemplateApprovalKey,
} from '@crm/shared';
import { cn } from '@crm/ui';
import { Check, CircleAlert, Clock, X } from 'lucide-react';
import type { MessageTemplate } from '@/modules/messages/api';

/**
 * Em que pe esta o modelo na Meta, em uma linha so: a etiqueta diz o estado e
 * o texto ao lado diz o que esperar dele. E o unico ponto da campanha que fala
 * de uma regra de fora - sem ele, a pessoa acha que o "Disparar" e que quebrou.
 */

const dia = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

const ESTILO: Record<TemplateApprovalKey, { icone: typeof Check; chip: string }> = {
  DRAFT: { icone: Clock, chip: 'bg-muted text-muted-foreground' },
  PENDING: { icone: Clock, chip: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  APPROVED: { icone: Check, chip: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  REJECTED: { icone: X, chip: 'bg-destructive/10 text-destructive' },
};

/** A etiqueta do estado, sozinha - usada na lista e ao lado do escolhido. */
export function ApprovalChip({ approval }: { approval: TemplateApprovalKey }) {
  const { icone: Icone, chip } = ESTILO[approval];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
        chip,
      )}
    >
      <Icone className="h-3.5 w-3.5" />
      {TEMPLATE_APPROVAL_LABELS[approval]}
    </span>
  );
}

export function TemplateStatus({ template }: { template: MessageTemplate | null }) {
  if (!template) {
    return (
      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <CircleAlert className="mt-px h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
        Sem modelo, esta campanha nao dispara em massa.
      </p>
    );
  }

  const quando = template.reviewedAt ?? template.submittedAt;

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <ApprovalChip approval={template.approval} />

        <span className="text-xs text-muted-foreground">
          {TEMPLATE_APPROVAL_HINTS[template.approval]}
          {quando ? ` · ${dia.format(new Date(quando))}` : ''}
        </span>
      </div>

      {/* O motivo da recusa e o unico caminho para consertar: sem ele, a pessoa
          reescreve no chute e leva outra recusa. */}
      {template.approval === 'REJECTED' && template.approvalNote ? (
        <p className="text-xs text-destructive">A Meta respondeu: {template.approvalNote}</p>
      ) : null}
    </div>
  );
}
